import { get } from "lodash";
import { PrimeIcons } from "primereact/api";
import { classNames } from "primereact/utils";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Modal } from "../../../../../common/components";
import { useUplinkInterval } from "../../../../devices/utils/hooks";
import { MODE_ICONS } from "../../../../energy/constants";
import { Mode } from "../../../../energy/models/mode.model";

interface ActiveModeModalProps {
    isOpen?: boolean;
    hasFanCoils?: boolean;
    hasRadiatorThermostats?: boolean;
    onClickClose?: VoidFunction;
    onSuccess?: VoidFunction;
    mode: Mode;
}

const ActiveModeModal = (props: ActiveModeModalProps) => {
    const {
        isOpen = false,
        hasFanCoils = false,
        hasRadiatorThermostats = false,
        onClickClose = () => {},
        mode,
    } = props;

    const { t } = useTranslation(["energy", "devices", "common"]);

    const { getFormattedUplinkInterval } = useUplinkInterval();

    return (
        <Modal
            className="max-w-4xl"
            isOpen={isOpen}
            onClickCancel={onClickClose}
            hasCancelButton
            title={
                <div className="flex gap-3.5">
                    {get(MODE_ICONS, mode.icon!, null)} {mode.name}
                </div>
            }
            onClickClose={onClickClose}
            cancelText={t("common:close")}
        >
            <div className="flex flex-col gap-5">
                <span className={twMerge("font-medium text-base leading-5", "mb-2.5")}>
                    {t("general.activeModeInfo")}
                </span>
                {mode.ruleOverride?.enabled && (
                    <span className="font-bold text-base leading-[1.375rem]">
                        {`${t("general.disableRulesSetTemperature")} ${
                            mode.ruleOverride.baseTemperature
                        }°C ${t("general.dailyResetAt")} ${
                            mode.ruleOverride.resetAt
                        }`}
                    </span>
                )}
                {mode.childLock && (
                    <span className="font-bold text-base leading-[1.375rem]">
                        {t("general.childLockActivated")}
                    </span>
                )}
                {mode.setUplinkInterval && (
                    <span className="font-bold text-base leading-[1.375rem]">
                        {`${t("general.setGlobalUplink")} ${
                            mode.uplinkIntervalInSeconds
                                ? getFormattedUplinkInterval(
                                      mode.uplinkIntervalInSeconds
                                  )
                                : t("common:default") || ""
                        }`}
                    </span>
                )}
                {hasFanCoils && (
                    <div className="flex gap-3.5">
                        <span className="font-bold text-base leading-[1.375rem]">
                            {t("general.operationModeForFanCoil")}
                        </span>
                        {t(`devices:fanCoilModes.${mode.fanCoilOperationMode}`)}
                    </div>
                )}
                {hasRadiatorThermostats && (
                    <div className="flex gap-3.5">
                        <span className="font-bold text-base leading-[1.375rem]">
                            {t("general.modeForThermostats")}
                        </span>
                        {t(
                            `devices:thermostatModes.${mode.radiatorOperationMode}`
                        )}
                    </div>
                )}
                <div className="flex gap-1.5 items-center mt-2.5">
                    <i
                        className={classNames(
                            PrimeIcons.EXCLAMATION_TRIANGLE,
                            "text-warning-500 text-lg"
                        )}
                    />
                    {t("general.activeModeWarning")}
                </div>
            </div>
        </Modal>
    );
};

export default ActiveModeModal;
